import React, { useEffect, useState } from "react";
import {
  Button,
  ButtonProps,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { IoChevronDown, IoPerson } from "react-icons/io5";
import clsx from "clsx";
import ReactGA from "react-ga";
import { useTranslation } from "react-i18next";
import { getTokenSilently, useAuth0 } from "react-auth0-spa";
import JGIcon from "Pages/Components/JGIcon";
import * as S from "Pages/Components/Header/AuthActions.styles";
import ACCOUNT_ROUTES from "Pages/Account/AccountRoutes";
import HeaderNavMenuItem from "Pages/Components/Header/HeaderNavMenuItem";
import SignOutDialog from "Pages/Components/Account/SignOutDialog";
import { User } from "../../../../types/Auth";
import { PlanAlertDialogProps } from "Pages/Components/PlanAlertDialog";
import SignInDialog from "../Account/SignInDialog";

/**
 * Donate-page (CheckoutV2 / CheckoutV3) variant of the header auth control.
 *
 * This is a standalone copy of the shared {@link import("Pages/Components/Header/AuthActions").default AuthActions}
 * so the donate redesign can render a filled "Log in/Sign up" button and, for
 * signed-in donors, a single "Log out" button — without touching the shared
 * site header component.
 */
type AuthActionsV2Props = ButtonProps & {
  alertDialog?: PlanAlertDialogProps;
  /** Donate page redesign: show a filled "Log in/Sign up" button. */
  donationPageV2?: boolean;
};

function getInitials(user: User): string | null {
  if (!user || !user.name) return null;
  const firstInitial = user.given_name?.[0]?.toUpperCase() || "";
  const lastInitial = user.family_name?.[0]?.toUpperCase() || "";
  return firstInitial + lastInitial || null;
}

const AuthActionsV2 = (props: AuthActionsV2Props) => {
  const { t } = useTranslation();
  const { className, alertDialog, donationPageV2, sx, ...rest } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const signInVariant = donationPageV2 ? "contained" : "outlined";
  const signInLabel = donationPageV2
    ? t("donationPage.logInSignUp")
    : t("donationPage.signInButton");
  // Donate page redesign button spec.
  const donationPageV2Sx = donationPageV2
    ? {
        fontFamily: `"DM Sans", sans-serif`,
        fontSize: "16px",
        fontWeight: 400,
        lineHeight: 1,
        textTransform: "none",
        color: "common.white",
        backgroundColor: "#7C6FE0",
        background: "#7C6FE0",
        boxShadow: "0px 4px 4px 0px #00000040",
        padding: "8px 12px",
        borderRadius: "16px",
        "&:hover, &:active": {
          backgroundColor: "#7C6FE0",
          boxShadow: "0px 4px 4px 0px #00000040",
        },
      }
    : undefined;
  // Combine the spec styling with any sx passed by the header (e.g. margin).
  const signInSx = [donationPageV2Sx, sx] as SxProps<Theme>;

  const { isAuthenticated, loading, loginAndReturn, logout, user } = useAuth0();
  const getIfUserNeedLogin = async () => {
    try {
      await getTokenSilently();
      // A valid token means the session is live — clear any stale latch so a
      // no-reload sign-in (e.g. popup) doesn't leave needLogin stuck on.
      setNeedLogin(false);
    } catch (error) {
      setNeedLogin(localStorage.getItem("hasAuthenticated") === "true");
    }
  };

  useEffect(() => {
    getIfUserNeedLogin();
  }, [isAuthenticated, loading, user]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [signOutDialogOpen, setSignOutDialogOpen] = useState(false);
  const [signInDialogOpen, setSignInDialogOpen] = useState(true);
  const [needLogin, setNeedLogin] = useState(false);

  const closeMenu = () => setAnchorEl(null);
  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const openAlertDialog = () => {
    if (!alertDialog) return;

    ReactGA.event({
      category: "button",
      action: "expiring",
      days: alertDialog.subscription?.termEnd.daysUntil,
    } as any);

    alertDialog.setOpen(true);
  };

  const isMenuOpen = Boolean(anchorEl);

  // Stale-session re-login prompt: only when we're NOT already authenticated.
  // If the context says we're signed in, fall through to the log-out UI.
  if (needLogin && signInDialogOpen && !isAuthenticated) {
    return (
      <>
        <SignInDialog
          open={signInDialogOpen}
          onClose={() => setSignInDialogOpen(false)}
          className={clsx(S.classes.root, className)}
          loading={loading}
          onClick={loginAndReturn}
        />
        {(!isAuthenticated || loading) && (
          <S.SignInButton
            variant={signInVariant}
            className={clsx(S.classes.root, className)}
            loading={loading}
            onClick={loginAndReturn}
            sx={signInSx}
            {...rest}
          >
            {signInLabel}
          </S.SignInButton>
        )}
      </>
    );
  }

  if (!isAuthenticated || loading) {
    return (
      <S.SignInButton
        variant={signInVariant}
        className={clsx(S.classes.root, className)}
        loading={loading}
        onClick={loginAndReturn}
        sx={signInSx}
        {...rest}
      >
        {signInLabel}
      </S.SignInButton>
    );
  }

  // Donate page redesign: signed-in donors get a single "Log out" button in
  // place of the avatar/account menu.
  if (donationPageV2) {
    // Must be a plain Button, NOT S.SignInButton: that component (JGSignInButton)
    // hardcodes onClick to loginWithRedirect and overrides any onClick passed in,
    // so using it here would make "Log out" actually trigger a login → consent.
    return (
      <Button
        variant="contained"
        className={clsx(S.classes.root, className)}
        sx={signInSx}
        {...rest}
        // Full logout: clears the Auth0 session (so a different user can sign in)
        // and returns to the app origin. The provider adds returnTo =
        // window.location.origin, which must be in Auth0's Allowed Logout URLs.
        onClick={() => logout()}
      >
        {t("donationPage.logOut")}
      </Button>
    );
  }

  const initials = getInitials(user);
  return (
    <>
      <S.AccountButton
        color="neutral"
        className={clsx(S.classes.root, className)}
        onClick={openMenu}
        {...rest}
      >
        {!isMobile && <S.StartIcon is={IoChevronDown} />}

        <S.AvatarBadge
          badgeContent={
            alertDialog?.subscription &&
            !alertDialog.subscription.renewButtonOnly
              ? 1
              : alertDialog?.freePlan ? 1 : undefined
          }
          color={"error"}
        >
          <S.UserAvatar>
            {initials ? (
              <Typography sx={{ fontWeight: "medium" }}>{initials}</Typography>
            ) : (
              <JGIcon is={IoPerson} />
            )}
          </S.UserAvatar>
        </S.AvatarBadge>
      </S.AccountButton>

      <Menu
        anchorEl={anchorEl}
        disableScrollLock
        open={isMenuOpen}
        onClose={closeMenu}
        onClick={closeMenu}
      >
        {alertDialog?.subscription &&
          !alertDialog.subscription.renewButtonOnly && (
            <S.PlanAlertMenuItem onClick={openAlertDialog} dense>
              {t("donationPage.planExpiring")}&nbsp;
              <strong>
                {alertDialog.subscription.termEnd.relativeFormattedDate}
              </strong>
            </S.PlanAlertMenuItem>
          )}
          {alertDialog?.freePlan && (
            <S.PlanAlertMenuItem onClick={openAlertDialog} dense>
              {t("donationPage.yourMembershipIs")}&nbsp;
              <strong>
                {t("donationPage.inactiveCaps")}
              </strong>
            </S.PlanAlertMenuItem>
          )}
        {ACCOUNT_ROUTES.map((route, index) => (
          <HeaderNavMenuItem
            key={route.label}
            link={{ to: `/account${route.to}`, exact: index === 0 }}
            label={index === 0 ? "Account" : route.label}
          />
        ))}

        <Divider />

        <MenuItem onClick={() => setSignOutDialogOpen(true)}>
          <ListItemText>{t("donationPage.signOut")}</ListItemText>
        </MenuItem>
      </Menu>

      <SignOutDialog
        open={signOutDialogOpen}
        onClose={() => setSignOutDialogOpen(false)}
      />
    </>
  );
};

export default AuthActionsV2;
